import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, XCircleIcon, DownloadIcon, SearchIcon, FilterIcon, MoreHorizontalIcon, SendIcon, RefreshCwIcon, FileTextIcon, InfoIcon, XIcon } from 'lucide-react';
import { PaymentItem, BulkPaymentBatch } from './BulkPayments';
export function BulkPaymentDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  // Mock batch data
  const batch: BulkPaymentBatch = {
    id: id || '1',
    name: 'December Invoices',
    createdAt: '2023-12-15T09:30:00',
    totalAmount: 12450.75,
    currency: 'USD',
    totalItems: 45,
    completedItems: 38,
    status: 'completed',
    collectionRate: 84.4
  };
  // Mock payment items
  const paymentItems: PaymentItem[] = [{
    id: '1',
    customerName: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 (555) 123-4567',
    amount: '349.99',
    currency: 'USD',
    description: 'Monthly subscription - Premium Plan',
    reference: 'INV-2023-12-001',
    status: 'paid'
  }, {
    id: '2',
    customerName: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 987-6543',
    amount: '199.50',
    currency: 'USD',
    description: 'Consulting services - December',
    reference: 'INV-2023-12-002',
    status: 'paid'
  }, {
    id: '3',
    customerName: 'Michael Brown',
    email: 'mbrown@example.com',
    phone: '+1 (555) 456-7890',
    amount: '599.00',
    currency: 'USD',
    description: 'Website development - Phase 1',
    reference: 'INV-2023-12-003',
    status: 'pending'
  }, {
    id: '4',
    customerName: 'Emily Wilson',
    email: 'e.wilson@example.com',
    phone: '+1 (555) 234-5678',
    amount: '149.99',
    currency: 'USD',
    description: 'Monthly subscription - Basic Plan',
    reference: 'INV-2023-12-004',
    status: 'paid'
  }, {
    id: '5',
    customerName: 'David Lee',
    email: 'david.lee@example.com',
    phone: '+1 (555) 876-5432',
    amount: '299.99',
    currency: 'USD',
    description: 'Marketing services - December',
    reference: 'INV-2023-12-005',
    status: 'expired'
  }, {
    id: '6',
    customerName: 'Jennifer Garcia',
    email: 'j.garcia@example.com',
    phone: '+1 (555) 345-6789',
    amount: '499.00',
    currency: 'USD',
    description: 'Product photography - Package B',
    reference: 'INV-2023-12-006',
    status: 'paid'
  }, {
    id: '7',
    customerName: 'Robert Martinez',
    email: 'r.martinez@example.com',
    phone: '+1 (555) 765-4321',
    amount: '799.50',
    currency: 'USD',
    description: 'Equipment rental - December',
    reference: 'INV-2023-12-007',
    status: 'paid'
  }];
  // Filter payments based on search and status filter
  const filteredPayments = paymentItems.filter(item => {
    const matchesSearch = item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || item.email.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()) || item.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? item.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  // Count payments by status
  const statusCounts = paymentItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5936">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-5937" />
            Paid
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-5938">
            <ClockIcon size={12} className="mr-1" data-id="element-5939" />
            Pending
          </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5940">
            <XCircleIcon size={12} className="mr-1" data-id="element-5941" />
            Expired
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-5942">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-5943" />
            Failed
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5944">
            <XCircleIcon size={12} className="mr-1" data-id="element-5945" />
            Cancelled
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" data-id="element-5946">
            {status}
          </span>;
    }
  };
  // Navigate back to the payment form with bulk tab selected
  const handleNavigateBack = () => {
    navigate('/payment-requests?tab=bulk');
  };
  const pendingCount = paymentItems.filter(p => p.status === 'pending').length;
  return <div className="space-y-8 pb-12" data-id="element-5947">
      {/* Back button */}
      <button onClick={handleNavigateBack} className="flex items-center text-[#F7941D] hover:text-[#D97B06] text-sm font-medium transition-colors" data-id="element-5948">
        <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-5949" />
        Back to Payment Form
      </button>

      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4" data-id="element-5950">
        <div data-id="element-5951">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-5952">{batch.name}</h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-5953">
            Created on {formatDate(batch.createdAt)}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-5954">
          <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors shadow-sm" data-id="element-5955">
            <DownloadIcon size={16} className="mr-2" data-id="element-5956" />
            Export
          </button>
          <button onClick={() => setShowReminderModal(true)} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-[#F7941D] hover:bg-[#D97B06] rounded-lg transition-colors shadow-sm" data-id="element-5957">
            <SendIcon size={16} className="mr-2" data-id="element-5958" />
            Send Reminders
          </button>
        </div>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-5959">
        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-5960">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-5961">
            Total Amount
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-5962">
            {batch.currency} {batch.totalAmount.toFixed(2)}
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-5963">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-5964">
            Collection Rate
          </div>
          <div className="flex items-center mt-1" data-id="element-5965">
            <div className="text-2xl font-bold text-gray-900 mr-3" data-id="element-5966">
              {batch.collectionRate}%
            </div>
            <div className="w-full max-w-[100px] bg-gray-100 rounded-full h-2" data-id="element-5967">
              <div className={`h-2 rounded-full ${batch.collectionRate >= 80 ? 'bg-green-500' : batch.collectionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
              width: `${batch.collectionRate}%`
            }} data-id="element-5968"></div>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1.5 font-medium" data-id="element-5969">
            {batch.completedItems} of {batch.totalItems} paid
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-5970">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-5971">Status</div>
          <div className="flex items-center mt-1" data-id="element-5972">
            {batch.status === 'completed' ? <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-5973" /> : batch.status === 'processing' ? <ClockIcon size={20} className="text-blue-500 mr-2" data-id="element-5974" /> : <AlertCircleIcon size={20} className="text-red-500 mr-2" data-id="element-5975" />}
            <span className="text-xl font-bold text-gray-900 capitalize" data-id="element-5976">
              {batch.status}
            </span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-5977">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-5978">
            Payment Requests
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-5979">
            {batch.totalItems}
          </div>
          <div className="flex items-center text-xs font-medium text-gray-500 mt-1.5" data-id="element-5980">
            <RefreshCwIcon size={12} className="mr-1" data-id="element-5981" />
            Last updated 5 mins ago
          </div>
        </div>
      </div>

      {/* Payment requests list */}
      <div className="pt-8 border-t border-gray-200" data-id="element-5982">
        <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-5983">
          Payment Requests
        </h3>

        {/* Search and filter */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0" data-id="element-5984">
          <div className="flex flex-wrap gap-2" data-id="element-5985">
            <button className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === null ? 'bg-[#F7941D]/10 text-[#F7941D]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setStatusFilter(null)} data-id="element-5986">
              All ({paymentItems.length})
            </button>
            {Object.entries(statusCounts).map(([status, count]) => <button key={status} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === status ? 'bg-[#F7941D]/10 text-[#F7941D]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setStatusFilter(status === statusFilter ? null : status)} data-id="element-5987">
                {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
              </button>)}
          </div>
          <div className="relative w-full sm:w-72" data-id="element-5988">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5989">
              <SearchIcon size={16} className="text-gray-400" data-id="element-5990" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#F7941D] focus:border-[#F7941D] text-sm" placeholder="Search payments..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-5991" />
          </div>
        </div>

        {/* Payments table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-5992">
          <div className="overflow-x-auto" data-id="element-5993">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5994">
              <thead className="bg-gray-50" data-id="element-5995">
                <tr data-id="element-5996">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5997">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5998">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5999">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6000">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6001">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6002">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6003">
                {filteredPayments.map(payment => <tr key={payment.id} className="hover:bg-gray-50 transition-colors group" data-id="element-6004">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6005">
                      <div className="text-sm font-medium text-gray-900" data-id="element-6006">
                        {payment.customerName}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-6007">
                        {payment.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6008">
                      <div className="text-sm font-medium text-gray-900" data-id="element-6009">
                        {payment.currency}{' '}
                        {parseFloat(payment.amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6010">
                      <div className="text-sm text-gray-500 max-w-xs truncate" data-id="element-6011">
                        {payment.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6012">
                      {payment.reference}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6013">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6014">
                      <div className="relative inline-block text-left" data-id="element-6015">
                        <button className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-6016">
                          <MoreHorizontalIcon size={18} data-id="element-6017" />
                        </button>
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 hidden group-hover:block" data-id="element-6018">
                          <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6019">
                            <SendIcon size={16} className="mr-3 text-gray-400" data-id="element-6020" />
                            Resend
                          </button>
                          <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6021">
                            <FileTextIcon size={16} className="mr-3 text-gray-400" data-id="element-6022" />
                            View Details
                          </button>
                          {payment.status === 'pending' && <button className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-6023">
                              <XCircleIcon size={16} className="mr-3 text-red-500" data-id="element-6024" />
                              Cancel Request
                            </button>}
                        </div>
                      </div>
                    </td>
                  </tr>)}
                {filteredPayments.length === 0 && <tr data-id="element-6025">
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-6026">
                      No payment requests found matching your filters.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reminder Confirmation Modal */}
      {showReminderModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-6027">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden" data-id="element-6028">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-6029">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-6030">
                Send Reminders
              </h3>
              <button onClick={() => setShowReminderModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-6031">
                <XIcon size={20} data-id="element-6032" />
              </button>
            </div>
            <div className="p-6" data-id="element-6033">
              <p className="text-sm text-gray-600" data-id="element-6034">
                This will send payment reminders to{' '}
                <span className="font-semibold text-gray-900" data-id="element-6035">
                  {pendingCount}
                </span>{' '}
                customers with pending payments.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3" data-id="element-6036">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors shadow-sm" onClick={() => setShowReminderModal(false)} data-id="element-6037">
                Cancel
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-[#F7941D] hover:bg-[#D97B06] rounded-lg transition-colors shadow-sm" onClick={() => setShowReminderModal(false)} data-id="element-6038">
                Send Reminders
              </button>
            </div>
          </div>
        </div>}
    </div>;
}